import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, startAfter, limit, DocumentData, getDoc, doc, updateDoc, where } from 'firebase/firestore';
import swal from 'sweetalert';

import { firebaseDB } from '../firebase/config';
import { LogoImageUserDefault } from '../components/Images';
import { ArchiveBoxArrowDownIcon, CheckBadgeIcon, NoSymbolIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-toastify';
import { getAuth } from 'firebase/auth';

const USERS_LIMIT = 20;
const REPORTS_LIMIT = 100;

const ReportsPage: React.FC = () => {
	const [reports, setReports] = useState<DocumentData[]>([]);
  const [lastUserDoc, setLastUserDoc] = useState<DocumentData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);

  useEffect(() => {
    loadReports();
  }, []);

	useEffect(() => {
		console.log('newReports ::', reports);
	}, [reports])

  const loadReports = async () => {
    if (!hasMore || loading) return;
    setLoading(true);

    try {
      const usersQuery = lastUserDoc
        ? query(collection(firebaseDB, 'users'), startAfter(lastUserDoc), limit(USERS_LIMIT))
        : query(collection(firebaseDB, 'users'), limit(USERS_LIMIT));

      const usersSnapshot = await getDocs(usersQuery);
      if (usersSnapshot.docs.length === 0) {
        setHasMore(false);
        setLoading(false);
        return;
      }

      const newReports: any[] = [];
      for (const userDoc of usersSnapshot.docs) {
        const reportedUsersRef = collection(firebaseDB, `users/${userDoc.id}/reportedUsers`);
        const reportedUsersSnapshot = await getDocs(query(reportedUsersRef, limit(REPORTS_LIMIT)));

				for (const reportDoc of reportedUsersSnapshot.docs) {
					const reportedUserId = reportDoc.data().reportedUser;
					const reportedUserDoc = await getDoc(doc(firebaseDB, 'users', reportedUserId));

					const newReport = {
						userId: userDoc.id,
						userInfo: userDoc.data(),
						reportedUserInfo: {
							...reportedUserDoc.data()
						},
						reportId: reportDoc.id,
            active: reportDoc.data().active ?? true,
						...reportDoc.data(),
					};

          console.log('newReport ::', newReport);
          

					if (!reports.some(report => report.reportId === newReport.reportId) && newReport?.active !== false) {
						newReports.push(newReport);	
					}
				}
      }

      setLastUserDoc(usersSnapshot.docs[usersSnapshot.docs.length - 1]);
 
			setReports((prevReports) => {
        const allReports = [...prevReports, ...newReports];
        const uniqueReports = Array.from(new Set(allReports.map(report => report.reportId)))
          .map(id => allReports.find(report => report.reportId === id));

        return uniqueReports;
      });
    } catch (error) {
      console.error('Error loading reports: ', error);
    }

    setLoading(false);
  };

	const handlerBlockUser = async (userId: string) => {
		try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        throw new Error('No authenticated user found.');
      }
      
      const idToken = await currentUser.getIdToken();

			if(!userId) {
				throw new Error('Invalid User ID');
			}

			const swalAction = await swal("Are you sure to block the reported user?", {
				dangerMode: true,
				buttons: ['No', 'Yes'],
			});
	
			if(swalAction)  {
        const userRef = doc(firebaseDB, 'users', userId);
        await updateDoc(userRef, { status: 'blocked' });

        const response = await fetch('https://us-central1-easymeet-26dd0.cloudfunctions.net/blockUser', {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify({ uid: userId }),
        });


        toast.success('User blocked successfully');

        setTimeout(() => {
          window.location.reload();
        }, 2000);
			}
		} catch(error: any) {
			toast.error(error.message || 'Error blocking user');
		}
	}

  const handlerArchive = async (userId: string, reportId: string) => {
		try {
			if(!userId) {
				throw new Error('Invalid User ID');
			}

			const swalAction = await swal("If you wish to archive the report you will no longer be able to view it in the list", {
				dangerMode: true,
				buttons: ['No', 'Yes'],
			});
	
			if(swalAction)  {
				const userRef = doc(firebaseDB, 'users', `${userId}/reportedUsers/${reportId}`);
				await updateDoc(userRef, {
          active: false
        });
				
				toast.success('Archive report successfully');

        setTimeout(() => {
          window.location.reload();
        }, 2000);
			}
		} catch(error) {
			toast.error('Error blocking user');
		}
	}
	
  return (
    <div className="min-h-screen p-10 mb-20">
			<div className="w-100 block pb-6 border-b-2 border-primary-700">
				<h1 className="font-bold text-2xl">Users Reports</h1>
			</div>

			<div className={'flex flex-col space-y-2 mt-6'}>
        <div className="overflow-x-auto mb-5">
					<table className={'table-auto table-custom'}>
          	<thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  REPORTING USER
                </th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  REPORTED USER
                </th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Date of report
                </th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reports.map((report, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {report.reportId.substring(0, 10)}
                  </td>
									<td style={{ maxWidth: '150px' }}>
										{ report.userInfo && <li className="flex justify-between gap-x-6 pr-1 pl-3">
											<div className="flex w-100">
												<img className="h-12 w-12 flex-none rounded-full bg-gray-50" src={report.userInfo.photoURL || LogoImageUserDefault} />
												<div className="min-w-0 flex-auto text-left ml-3">
													<p className="text-sm font-semibold leading-6 text-gray-900">{ report.userInfo.name }</p>
													<p className="truncate text-xs leading-5 text-gray-500"><p>{ report.userInfo.email }</p></p>
												</div>
											</div>
										</li>}
									</td>
                  <td>
										{ report.reportedUserInfo && <li className="flex justify-between gap-x-6 pr-1 pl-3">
												<div className="flex w-100">
													<img className="h-12 w-12 flex-none rounded-full bg-gray-50" src={report.reportedUserInfo.photoURL || LogoImageUserDefault} />
													<div className="min-w-0 flex-auto text-left ml-3">
														<p className="text-sm font-semibold leading-6 text-gray-900">{ report.reportedUserInfo.name }</p>
														<p className="truncate text-xs leading-5 text-gray-500"><p>{ report.reportedUserInfo.email }</p></p>
													</div>
												</div>
											</li>
										}
									</td>
									<td>
										<p className="text-sm text-gray-500">{ new Date(report.createdAt).toLocaleDateString() }</p>
									</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
										{ (report.reportedUserInfo?.status == 'blocked' && report.reportedUserInfo?.status) ? 'Blocked' : 'Pending' }
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 truncate max-w-sm">
                    { report.reportedUserInfo?.status != 'blocked' && <button 
												type="button"
												className="btn btn-sm btn-icon btn-danger mr-2"
												title='Block User'
												onClick={() => handlerBlockUser(report.reportedUser)}
											>
												<NoSymbolIcon className="size-4"/>
											</button>

                    }

                    { report.active && <button 
												type="button"
												className="btn btn-sm btn-icon btn-warning active mr-2"
												title='Archive Report'
												onClick={() => handlerArchive(report.userId, report.reportId)}
											>
												<ArchiveBoxArrowDownIcon className="size-4 text-dark"/>
											</button>

                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && (
          <div className="flex justify-center mt-6">
            <svg
              className="animate-spin h-8 w-8 text-blue-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8l3.5 3.5a7.97 7.97 0 01-11.5-3.5z"
              ></path>
            </svg>
          </div>
        )}

        {!loading && hasMore && (
          <div className="flex justify-center mt-6">
            <button
              onClick={loadReports}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg shadow-md"
            >
              Load More
            </button>
          </div>
        )}

        {!hasMore && !loading && (
          <p className="text-center text-gray-500 mt-6">
            No more reports to load
          </p>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;
